import React, { useState, useEffect } from 'react';
import { Mail, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { sendPasswordResetEmail } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { auth } from '@/lib/firebase';

interface SignInFormProps {
  onCreateAccount: () => void;
  onSuccessfulLogin: () => void;
  initialEmail?: string;
}

export default function SignInForm({ onCreateAccount, onSuccessfulLogin, initialEmail = '' }: SignInFormProps) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: initialEmail,
    password: '',
    remember: false
  });
  const [error, setError] = useState<React.ReactNode>('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const { signIn, logout } = useAuth();

  // Update email if initialEmail prop changes
  useEffect(() => {
    if (initialEmail) {
      setFormData(prev => ({ ...prev, email: initialEmail }));
    }
  }, [initialEmail]);

  const validateForm = () => {
    if (!formData.email.trim()) {
      setError('Please enter your email address');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!formData.password) {
      setError('Please enter your password');
      return false;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) return;

    setLoading(true);

    try {
      // Clear any existing auth state first
      await logout();
      
      try {
        const result = await signIn(formData.email.trim(), formData.password, formData.remember);
        if (result) {
          onSuccessfulLogin();
        }
      } catch (error: any) {
        if (error.message === 'APPROVAL_PENDING') {
          navigate('/approval-pending');
          return;
        }
        throw error;
      }
    } catch (err: any) {
      if (err.message === 'Invalid email or password') {
        setError(
          <span>
            Invalid email or password. Need an account?{' '}
            <button 
              onClick={onCreateAccount}
              className="text-primary hover:text-primary-hover underline"
            >
              Sign up here
            </button>
          </span>
        );
        return;
      }
      console.error('Error during sign in:', err);
      setError(err.message || 'Failed to sign in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!formData.email.trim()) {
      setError('Please enter your email address first');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setError('');
    setSuccess('');
    setResetLoading(true);

    try {
      await sendPasswordResetEmail(auth, formData.email.trim());
      setSuccess('Password reset email sent! Please check your inbox.');
      setError('');
    } catch (err: any) {
      console.error('Error sending reset email:', err);
      
      switch (err.code) {
        case 'auth/user-not-found':
          setError('No account found with this email address.');
          break;
        case 'auth/invalid-email':
          setError('Please enter a valid email address.');
          break;
        case 'auth/too-many-requests':
          setError('Too many requests. Please try again later.');
          break;
        default:
          setError('Failed to send reset email. Please try again.');
      }
    } finally {
      setResetLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData(prev => ({
      ...prev,
      [e.target.name]: value
    }));

    // Clear messages when user starts typing
    if (error) setError('');
    if (success) setSuccess('');
  };

  return (
    <div className="max-w-md w-full space-y-8">
      <div className="text-center">
        <h2 className="mt-6 text-3xl font-bold text-gray-900">Welcome back</h2>
        <p className="mt-2 text-sm text-gray-600">
          Continue your professional growth journey
        </p>
      </div>
      
      <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg text-sm">
            {success}
          </div>
        )}

        <div className="space-y-4">
          <div className="relative flex items-center">
            <Mail className="absolute left-3 w-5 h-5 text-[#151b59] z-10" />
            <input
              type="email"
              name="email"
              required
              className="appearance-none rounded-lg block w-full pl-10 pr-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
              placeholder="Email address"
              value={formData.email}
              onChange={handleChange}
              disabled={loading || resetLoading}
            />
          </div>

          <div className="relative flex items-center">
            <Lock className="absolute left-3 w-5 h-5 text-[#151b59] z-10" />
            <input
              type="password"
              name="password"
              required
              className="appearance-none rounded-lg block w-full pl-10 pr-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
              placeholder="Password"
              value={formData.password}
              onChange={handleChange}
              disabled={loading || resetLoading}
            />
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              id="remember"
              name="remember"
              type="checkbox"
              className="h-4 w-4 text-[#151b59] focus:ring-primary border-gray-300 rounded"
              checked={formData.remember}
              onChange={handleChange}
              disabled={loading || resetLoading}
            />
            <label htmlFor="remember" className="ml-2 block text-sm text-gray-900">
              Remember me
            </label>
          </div>

          <div className="text-sm">
            <button
              type="button"
              onClick={handlePasswordReset}
              disabled={loading || resetLoading}
              className="font-medium text-[#151b59] hover:text-[#1c236e] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resetLoading ? 'Sending...' : 'Forgot your password?'}
            </button>
          </div>
        </div>

        <div>
          <button
            type="submit"
            disabled={loading || resetLoading}
            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-dark bg-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </div>

      </form>
    </div>
  );
}